import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate, Outlet } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import { PdfJobProvider } from "./context/PdfJobContext";
import { ToastProvider } from "./components/Dashboard/shared/CustomToast";
import "./App.css";
import DashboardDetailsPage from "./components/Dashboard/Dashboard/Views/DashboardTables/DashboardDetail/DashboardDetailsPage";

// Fix for ResizeObserver loop limit exceeded error
if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    if (args[0]?.includes?.('ResizeObserver loop completed with undelivered notifications.')) {
      return;
    }
    originalError.apply(console, args);
  };

  window.addEventListener('error', (e) => {
    if (e.message?.includes('ResizeObserver loop completed with undelivered notifications.')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
}

/* ===================== Lazy Imports ===================== */

// Core
const Home = lazy(() => import("./components/Home"));
const Layout = lazy(() => import("./Layout"));
const Contact = lazy(() => import("./components/Home/components/Contact"));
const PricingPage = lazy(() => import("./components/Home/components/PricingPage"));
const DemoPage = lazy(() => import("./components/Home/components/Demo/DemoPage"));

// Common Masters
const Agency = lazy(() => import("./components/Dashboard/Common/Agency"));
const AddAgency = lazy(() => import("./components/Dashboard/Common/Agency/AddAgency"));
const Branch = lazy(() => import("./components/Dashboard/Common/Branch"));
const AddBranch = lazy(() => import("./components/Dashboard/Common/Branch/AddBranch"));
const VehicleMaster = lazy(() => import("./components/Dashboard/GeneralInsurance/VehicleMaster"));
const AddVehicleMaster = lazy(() => import("./components/Dashboard/GeneralInsurance/VehicleMaster/AddVehicleMaster"));
const CustomerMasterList = lazy(() => import("./components/Dashboard/Common/CustomerMaster"));
const AddCustomerMaster = lazy(() => import("./components/Dashboard/Common/CustomerMaster/AddCustomerMaster"));
const CustomerBrowser = lazy(() => import("./components/Dashboard/Common/CustomerBrowser/CustomerBrowser"));
const MergeGroup = lazy(() => import("./components/Dashboard/Common/MergeGroup/MergeGroup"));
const MergeCustomer = lazy(() => import("./components/Dashboard/Common/MergeCustomer/MergeCustomer"));
const GroupMaster = lazy(() => import("./components/Dashboard/Common/Groupmaster"));
const AddGroupMaster = lazy(() => import("./components/Dashboard/Common/Groupmaster/AddGroupMaster"));
const AreaMaster = lazy(() => import("./components/Dashboard/Common/AreaMaster"));
const AddAreaMaster = lazy(() => import("./components/Dashboard/Common/AreaMaster/AddAreaMaster"));
const CRMGroupMaster = lazy(() => import("./components/Dashboard/Common/CRMGroupMaster"));
const AddCRMGroupMaster = lazy(() => import("./components/Dashboard/Common/CRMGroupMaster/AddCRMGroupMaster"));
const ActiveInactiveCustomerList = lazy(() =>
  import("./components/Dashboard/Common/activeInactiveCustomerList/ActiveInactiveCustomerList")
);
const AreaMapping = lazy(() => import("./components/Dashboard/Common/AreaMapping"));

// Admin
const RoleMaster = lazy(() => import("./components/Dashboard/Admin/Roles"));
const AddRole = lazy(() => import("./components/Dashboard/Admin/Roles/AddRole"));
const RolePrivilege = lazy(() => import("./components/Dashboard/Admin/RolePrivilege"));
const AddRolePrivilege = lazy(() => import("./components/Dashboard/Admin/RolePrivilege/AddRolePrivilege"));
const EmployeeMasterList = lazy(() => import("./components/Dashboard/Admin/EmployeeMaster"));
const AddEmployee = lazy(() => import("./components/Dashboard/Admin/EmployeeMaster/AddEmployee"));
const AddThemeMaster = lazy(() => import("./components/Dashboard/Admin/ThemeMaster/AddThemeMaster"));
const CommunicationMaster = lazy(() => import("./components/Dashboard/Admin/CommunicationMaster/communicationMaster"));
const CommunicationSchedule = lazy(() => import("./components/Dashboard/Admin/CommunicationMaster/CommunicationSchedule"));
const CommunicationLog = lazy(() => import("./components/Dashboard/Admin/CommunicationLog/CommunicationLog"));
const CreditsDashboard = lazy(() => import("./components/Dashboard/Admin/CommunicationLog/CommunicationCredits"));
const ErrorTracker = lazy(() => import("./components/Dashboard/Admin/ErrorTracker"));
const ApplicationConfigure = lazy(() => import("./components/Dashboard/Admin/ApplicationConfigure/ApplicationConfigure"));
const DocumentManager = lazy(() => import("./components/Dashboard/Admin/DocumentManager/DocumentManager"));
const CustomTemplates = lazy(() => import("./components/Dashboard/Admin/CustomTemplates"));

// General Insurance
const OtherProduct = lazy(() => import("./components/Dashboard/GeneralInsurance/OtherProduct"));
const AddOtherProduct = lazy(() => import("./components/Dashboard/GeneralInsurance/OtherProduct/AddOtherProduct"));
const OtherInsuranceEntryList = lazy(() => import("./components/Dashboard/GeneralInsurance/OtherInsuranceEntry"));
// const AddOtherInsuranceEntry = lazy(() => import("./components/Dashboard/GeneralInsurance/OtherInsuranceEntry/AddOtherInsuranceEntry"));
const MotorProduct = lazy(() => import("./components/Dashboard/GeneralInsurance/MotorProduct"));
const AddMotorProduct = lazy(() => import("./components/Dashboard/GeneralInsurance/MotorProduct/AddMotorProduct"));
const MotorAddOns = lazy(() => import("./components/Dashboard/GeneralInsurance/MotorAddOns"));
const AddMotorAddOns = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/MotorAddOns/AddMototAddons")
);
const VehicleMakeMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/VehicleMakeMaster")
);
const AddVehicleMakeMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/VehicleMakeMaster/AddVehicleMakeMaster")
);
const VehicleModelMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/VehicleModel")
);
const AddVehicleModelMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/VehicleModel/AddVehicleModel")
);
// const VehicleMaster = lazy(() => import("./components/Dashboard/GeneralInsurance/VehicleMaster"));
// const AddVehicleMaster = lazy(() => import("./components/Dashboard/GeneralInsurance/VehicleMaster/AddVehicleMaster"));
const HealthInsuranceQuotation = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/HealthInsuranceQuotation")
);
const AddHealthInsuranceQuotation = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/HealthInsuranceQuotation/AddHealthInsuranceQuotation")
);
const OtherInsuranceQuotation = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/OtherInsuranceQuotation")
);
const AddOtherInsuranceQuotation = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/OtherInsuranceQuotation/AddOtherInsuranceQuotation")
);
const ConsolidatedRenewalList = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/consolidatedRenewal/ConsolidatedRenewalList")
);
const RenewalIntimation = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/renewalIntimation/RenewalIntimation")
);
const EndorsementIntimation = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/EndorsementIntimation/EndorsementIntimation")
);
const PolicyRegister = lazy(() =>
  import('./components/Dashboard/GeneralInsurance/PolicyRegister/PolicyRegister')
);
const GICPolicyReport = lazy(() =>
  import('./components/Dashboard/GeneralInsurance/PolicyRegister/GICPolicyReport')
);
const GICPolicyReportPreview = lazy(() =>
  import('./components/Dashboard/GeneralInsurance/PolicyRegister/GICPolicyReportPreview')
);
const PolicyNotReceivedList = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/PolicyNotReceived")
);
const TPAMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/TPAMaster")
);
const HealthInsuranceEntryList = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/HealthInsuranceEntry")
);
const HealthInsuranceEntry = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/HealthInsuranceEntry/HealthInsuranceEntry")
);

const MotorInsuranceEntryList = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/MotorInsuranceEntry")
);
const MotorInsuranceEntry = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/MotorInsuranceEntry/MotorInsuranceEntry")
);

// Commission Master
const CommissionMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/CommissionMaster")
);
const AddCommissionMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/CommissionMaster/AddCommissionMaster")
);

// Life Insurance
const LifeProductMasterList = lazy(() =>
  import("./components/Dashboard/LifeInsurance/LifeProductMaster")
);

const Dashboard = lazy(() =>
  import("./components/Dashboard/Dashboard/Dashboard")
);

const AddLifeProductMaster = lazy(() =>
  import("./components/Dashboard/LifeInsurance/LifeProductMaster/AddLifeProductMaster")
);
const LifePolicyList = lazy(() =>
  import("./components/Dashboard/LifeInsurance/LifePolicyEntry")
);
const AddLifePolicyEntry = lazy(() =>
  import("./components/Dashboard/LifeInsurance/LifePolicyEntry/AddLifePolicyEntry")
);
const PremiumDepositEntryList = lazy(() =>
  import("./components/Dashboard/LifeInsurance/PremiumDepositEntry")
);
const AddPremiumDepositEntry = lazy(() =>
  import("./components/Dashboard/LifeInsurance/PremiumDepositEntry/AddPremiumDepositEntry")
);
const BatchFupUpdate = lazy(() =>
  import("./components/Dashboard/LifeInsurance/FUP/BatchFupUpdate")
);
const LifeInsuranceImportPolicyData = lazy(() =>
  import("./components/Dashboard/LifeInsurance/LifeInsuranceImportPolicyData/LifeInsuranceImportPolicyData")
);
const ImportPdf = lazy(() =>
  import("./components/Dashboard/LifeInsurance/ImportPdf/ImportPdf")
);
const ImportSummary = lazy(() =>
  import("./components/Dashboard/LifeInsurance/LifeInsuranceImportPolicyData/ImportSummary")
);
const GIPolicyImport = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/GIPolicyImport/GIPolicyImport")
);
const GISummary = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/GIPolicyImport/GISummary")
);


// Phase-2 Additions
const HealthProductMaster = lazy(() =>
  import("./components/Dashboard/HealthProduct/HealthProductMaster")
);
const HealthProductList = lazy(() =>
  import("./components/Dashboard/HealthProduct")
);
const HealthProductBenefitMaster = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/HealthProductBenefitMaster")
);
const HealthBenefitsComparison = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/HealthBenefitsComparison")
);
const AddNewProfile = lazy(() =>
  import("./components/Dashboard/MyProfile/AddNewProfile")
);
const CommissionReceiptEntryList = lazy(() =>
  import("./components/Dashboard/LifeInsurance/CommissionReceiptEntry")
);
const AddCommissionReceiptEntry = lazy(() =>
  import("./components/Dashboard/LifeInsurance/CommissionReceiptEntry/AddCommissionReceiptEntry")
);
const CommissionBillImport = lazy(() =>
  import("./components/Dashboard/LifeInsurance/CommissionBillImport")
);
const ImportCommissionExcel = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/ImportCommissionExcel")
);
const CommissionReconciliation = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/CommissionReconciliation")
);
const CommissionOutstanding = lazy(() =>
  import("./components/Dashboard/GeneralInsurance/CommissionOutstanding")
);


// Life Insurance Reports
const LifePolicyRegister = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/LifePolicyRegister/LifePolicyRegister"));
const PolicyReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/LifePolicyRegister/PolicyReport"));
const PolicyReportPreview = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/LifePolicyRegister/PolicyReportPreview"));
const PremiumDueReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/PremiumDueReport/PremiumDueReport"));
const PremiumDueReportGenerate = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/PremiumDueReport/PremiumDueReportGenerate"));
const PremiumOutstandingReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/PremiumOutstandingReport/PremiumOutstandingReport"));
const PremiumOutstandingReportPreview = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/PremiumOutstandingReport/PremiumOutstandingReportPreview"));
const LapsedPoliciesReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/LapsedPoliciesReport/LapsedPoliciesReport"));
const LapsedPoliciesReportGenerate = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/LapsedPoliciesReport/LapsedPoliciesReportGenerate"));
const CurrentStatusReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/CurrentStatusReport/CurrentStatusReport"));
const CurrentStatusReportPreview = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/CurrentStatusReport/CurrentStatusReportPreview"));
const MaturityPoliciesReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/MaturityPoliciesReport/MaturityPoliciesReport"));
const MaturityPoliciesReportPreview = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/MaturityPoliciesReport/MaturityPoliciesReportPreview"));
const CashFlowReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/CashFlowReport/CashFlowReport"));
const CashFlowReportPreview = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/CashFlowReport/CashFlowReportPreview"));
const DrillDownReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/DrillDownReport/DrillDownReport"));
const SurvivalBenefitReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/SurvivalBenefitReport/SurvivalBenefitReport"));
const PremiumCalendarReport = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/PremiumCalendarReport/PremiumCalendarReport"));
const PremiumCalendarReportPreview = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/PremiumCalendarReport/PremiumCalendarReportPreview"));
const ComprehensiveInsuranceChart = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/ComprehensiveInsuranceChart/ComprehensiveInsuranceChart"));
const CommissionSummaryReport = lazy(() => import("./components/Dashboard/LifeInsurance/LIC_Comm_Reports/CommisionSummaryReport"));
const BonusCommissionEntryList = lazy(() => import("./components/Dashboard/LifeInsurance/BonusCommisionEntry"));
const AddBonusCommissionEntry = lazy(() => import("./components/Dashboard/LifeInsurance/BonusCommisionEntry/AddBonusCommisionEntry"));

const SurvivalBenefitReportPreview = lazy(() => import("./components/Dashboard/LifeInsurance/AllReportsLifeInsurance/SurvivalBenefitReport/SurvivalBenefitReportPreview"));
const OtherInsuranceEntry = lazy(() => import("./components/Dashboard/GeneralInsurance/OtherInsuranceEntry"));
const AddOtherInsuranceEntry = lazy(() => import("./components/Dashboard/GeneralInsurance/OtherInsuranceEntry/AddOtherInsuranceEntry"));
const ResetPassword = lazy(() => import("./components/Auth/ResetPassword/ResetPassword"));
const ForgotPassword = lazy(() => import("./components/Auth/ForgotPassword/ForgotPassword"));

/* ===================== GIC Route Guard ===================== */
const GICRouteGuard = () => {
  const { user } = useSelector((state) => state.auth);
  if (user && !user.gICAccess) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
};

/* ===================== App ===================== */
function App() {
  const { token, isInitialized } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-redirect if not logged in
  useEffect(() => {
    // Wait for the initial silent refresh to complete
    if (!isInitialized) return;

    const publicPaths = ["/", "/contact", "/pricing", "/demo", "/dashboard", "/reset-password", "/forgot-password"];
    if (!token && !publicPaths.includes(location.pathname)) {
      navigate("/");
    }
  }, [token, isInitialized, location, navigate]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={2000} />

      <ToastProvider>
        <PdfJobProvider>
          <Suspense fallback={<div className="text-center mt-5">Loading...</div>}>
            <Routes>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/demo" element={<Dashboard />} />
              <Route path="/dashboard/details" element={<DashboardDetailsPage />} />

              <Route path="agency" element={<Agency />} />
              <Route path="agency/add" element={<AddAgency />} />
              <Route path="agency/edit/:id" element={<AddAgency />} />
              <Route path="agency/view/:id" element={<AddAgency />} />

              <Route path="branch" element={<Branch />} />
              <Route path="branch/add" element={<AddBranch />} />
              <Route path="branch/edit/:id" element={<AddBranch />} />
              <Route path="branch/view/:id" element={<AddBranch />} />

              <Route path="customer" element={<CustomerMasterList />} />
              <Route path="customer-master/add" element={<AddCustomerMaster />} />
              <Route path="customer-master/edit/:id" element={<AddCustomerMaster />} />
              <Route path="customer-master/view/:id" element={<AddCustomerMaster />} />
              <Route path="customer-browser" element={<CustomerBrowser />} />
              <Route path="area-mapping" element={<AreaMapping />} />

              <Route path="merge-group" element={<MergeGroup />} />
              <Route path="merge-customer" element={<MergeCustomer />} />

              <Route path="group" element={<GroupMaster />} />
              <Route path="group-master/add" element={<AddGroupMaster />} />
              <Route path="group-master/edit/:id" element={<AddGroupMaster isUpdateMode />} />
              <Route path="group-master/view/:id" element={<AddGroupMaster isUpdateMode />} />

              <Route path="area-master-list" element={<AreaMaster />} />
              <Route path="area-master/add" element={<AddAreaMaster />} />
              <Route path="area-master/view/:id" element={<AddAreaMaster />} />
              <Route path="area-master/edit/:id" element={<AddAreaMaster />} />

              <Route path="crm-group-list" element={<CRMGroupMaster />} />
              <Route path="crm-group/add" element={<AddCRMGroupMaster />} />
              <Route path="crm-group/edit/:id" element={<AddCRMGroupMaster />} />
              <Route path="crm-group/view/:id" element={<AddCRMGroupMaster />} />

              <Route path="roles" element={<RoleMaster />} />
              <Route path="role/add" element={<AddRole />} />
              <Route path="role-privilege" element={<RolePrivilege />} />
              <Route path="role-privilege/add" element={<AddRolePrivilege />} />

              <Route path="employee-master" element={<EmployeeMasterList />} />
              <Route path="employee-master/add" element={<AddEmployee />} />
              <Route path="employee-master/edit/:id" element={<AddEmployee />} />
              <Route path="employee-master/view/:id" element={<AddEmployee />} />

              <Route path="theme-master" element={<AddThemeMaster />} />
              <Route path="communication-master" element={<CommunicationMaster />} />
              <Route path="communication-schedule" element={<CommunicationSchedule />} />
              <Route path="communication-logs" element={<CommunicationLog />} />
              <Route path="custom-templates" element={<CustomTemplates />} />
              <Route path="credits-dashboard" element={<CreditsDashboard />} />
              <Route path="error-tracker" element={<ErrorTracker />} />
              <Route path="application-configure" element={<ApplicationConfigure />} />
              <Route path="document-manager" element={<DocumentManager />} />
              <Route path="my-profile" element={<AddNewProfile />} />

              <Route path="life-product" element={<LifeProductMasterList />} />
              <Route path="life-product/add" element={<AddLifeProductMaster />} />
              <Route path="life-product/edit/:id" element={<AddLifeProductMaster />} />
              <Route path="life-product/view/:id" element={<AddLifeProductMaster />} />

              <Route path="life-policy-entry" element={<LifePolicyList />} />
              <Route path="life-policy-entry/add" element={<AddLifePolicyEntry />} />
              <Route path="life-policy-entry/edit/:id" element={<AddLifePolicyEntry isUpdateMode />} />
              <Route path="life-policy-entry/view/:id" element={<AddLifePolicyEntry isUpdateMode />} />

              <Route path="premium-deposit-entry" element={<AddPremiumDepositEntry />} />
              <Route path="premium-deposit-entry-list" element={<PremiumDepositEntryList />} />
              <Route path="premium-deposit-entry/edit/:id" element={<AddPremiumDepositEntry />} />
              <Route path="premium-deposit-entry/view/:id" element={<AddPremiumDepositEntry />} />

              <Route path="commission-receipt-entry" element={<CommissionReceiptEntryList />} />
              <Route path="commission-receipt-entry/add" element={<AddCommissionReceiptEntry />} />
              <Route path="commission-receipt-entry/edit/:id" element={<AddCommissionReceiptEntry />} />
              <Route path="commission-receipt-entry/view/:id" element={<AddCommissionReceiptEntry />} />

              <Route path="commission-bill-import" element={<CommissionBillImport />} />
              <Route path="commision-summary-report" element={<CommissionSummaryReport />} />
              <Route path="bonus-commission-entry-list" element={<BonusCommissionEntryList />} />
              <Route path="add-bonus-commission-entry" element={<AddBonusCommissionEntry />} />
              <Route path="bonus-commission-entry/edit/:id" element={<AddBonusCommissionEntry />} />
              <Route path="bonus-commission-entry/view/:id" element={<AddBonusCommissionEntry />} />

              <Route path="import-policy-report" element={<LifeInsuranceImportPolicyData />} />
              <Route path="import-policy-summary/:batchId" element={<ImportSummary />} />
              <Route path="import-gi-policy" element={<GIPolicyImport />} />
              <Route path="import-gi-policy-summary/:batchId" element={<GISummary />} />
              <Route path="import-policy-pdf" element={<ImportPdf />} />

              <Route path="fup-update" element={<BatchFupUpdate />} />
              <Route path="life-policy-register" element={<LifePolicyRegister />} />

              <Route path="policy-report" element={<PolicyReportPreview />} />
              <Route path="gic-policy-report-preview" element={<GICPolicyReportPreview />} />
              <Route path="premium-due-report" element={<PremiumDueReport />} />
              <Route path="premium-due-report-preview" element={<PremiumDueReportGenerate />} />
              <Route path="premium-outstanding-report" element={<PremiumOutstandingReport />} />
              <Route path="premium-outstanding-report-preview" element={<PremiumOutstandingReportPreview />} />
              <Route path="lapsed-policy-report" element={<LapsedPoliciesReport />} />
              <Route path="lapsed-policies-report-preview" element={<LapsedPoliciesReportGenerate />} />
              <Route path="current-status-report" element={<CurrentStatusReport />} />
              <Route path="current-status-report-preview" element={<CurrentStatusReportPreview />} />
              <Route path="maturity-policies-report" element={<MaturityPoliciesReport />} />
              <Route path="maturity-policies-report-preview" element={<MaturityPoliciesReportPreview />} />
              <Route path="cash-flow-report" element={<CashFlowReport />} />
              <Route path="cash-flow-report-preview" element={<CashFlowReportPreview />} />
              <Route path="drill-down-report" element={<DrillDownReport />} />
              <Route path="survival-benefit-report" element={<SurvivalBenefitReport />} />
              <Route path="survival-benefit-report-preview" element={<SurvivalBenefitReportPreview />} />
              <Route path="premium-calendar-report" element={<PremiumCalendarReport />} />
              <Route path="premium-calendar-report-preview" element={<PremiumCalendarReportPreview />} />
              <Route path="comprehensive-insurance-chart" element={<ComprehensiveInsuranceChart />} />

              <Route path="active-inactive-customer" element={<ActiveInactiveCustomerList />} />

              {/* General Insurance Protected Routes */}
              <Route element={<GICRouteGuard />}>
                <Route path="vehicle-master" element={<VehicleMaster />} />
                <Route path="vehicle-master/add" element={<AddVehicleMaster />} />
                <Route path="vehicle-master/edit/:id" element={<AddVehicleMaster />} />
                <Route path="vehicle-master/view/:id" element={<AddVehicleMaster />} />

                <Route path="other-insurance-entry" element={<OtherInsuranceEntryList />} />
                <Route path="other-insurance-entry/add" element={<AddOtherInsuranceEntry />} />
                <Route path="other-insurance-entry/edit/:id" element={<AddOtherInsuranceEntry />} />
                <Route path="other-insurance-entry/view/:id" element={<AddOtherInsuranceEntry />} />

                <Route path="health-insurance-quotation" element={<HealthInsuranceQuotation />} />
                <Route path="health-insurance-quotation/add" element={<AddHealthInsuranceQuotation />} />
                <Route path="health-insurance-quotation/edit/:id" element={<AddHealthInsuranceQuotation />} />
                <Route path="health-insurance-quotation/:view/:id" element={<AddHealthInsuranceQuotation />} />

                <Route path="motor-addOn-master" element={<MotorAddOns />} />
                <Route path="motor-addOn-master/add" element={<AddMotorAddOns />} />
                <Route path="motor-addOn-master/edit/:id" element={<AddMotorAddOns />} />
                <Route path="motor-addOn-master/view/:id" element={<AddMotorAddOns />} />

                <Route path="motor-product-master" element={<MotorProduct />} />
                <Route path="motor-product-master/add" element={<AddMotorProduct />} />
                <Route path="motor-product-master/edit/:id" element={<AddMotorProduct />} />
                <Route path="motor-product-master/view/:id" element={<AddMotorProduct />} />

                <Route path="other-product-master">
                  <Route index element={<OtherProduct />} />
                  <Route path="add" element={<AddOtherProduct />} />
                  <Route path="edit/:id" element={<AddOtherProduct />} />
                  <Route path="view/:id" element={<AddOtherProduct />} />
                </Route>

                <Route path="gi-commission-master">
                  <Route index element={<CommissionMaster />} />
                  <Route path="add" element={<AddCommissionMaster />} />
                  <Route path="edit/:commissionId" element={<AddCommissionMaster />} />
                  <Route path="view/:commissionId" element={<AddCommissionMaster />} />
                </Route>

                <Route path="vehicle-make-master" element={<VehicleMakeMaster />} />
                <Route path="vehicle-make-master/add" element={<AddVehicleMakeMaster />} />
                <Route path="vehicle-make-master/edit/:id" element={<AddVehicleMakeMaster />} />

                <Route path="vehicle-model-master" element={<VehicleModelMaster />} />
                <Route path="vehicle-model-master/add" element={<AddVehicleModelMaster />} />
                <Route path="vehicle-model-master/edit/:id" element={<AddVehicleModelMaster />} />

                <Route path="other-insurance-quotation" element={<OtherInsuranceQuotation />} />
                <Route path="other-insurance-quotation/add" element={<AddOtherInsuranceQuotation />} />
                <Route path="other-insurance-quotation/edit/:id" element={<OtherInsuranceQuotation />} />
                <Route path="other-insurance-quotation/:view/:id" element={<AddOtherInsuranceQuotation />} />

                <Route path="import-commission-excel" element={<ImportCommissionExcel />} />
                <Route path="commission-reconciliation" element={<CommissionReconciliation />} />
                <Route path="gi-commission-outstanding" element={<CommissionOutstanding />} />

                <Route path="health-product" element={<HealthProductList />} />
                <Route path="health-product/add" element={<HealthProductMaster />} />
                <Route path="health-product/edit/:id" element={<HealthProductMaster />} />
                <Route path="health-product/view/:id" element={<HealthProductMaster />} />

                <Route path="health-product-benefit-master" element={<HealthProductBenefitMaster />} />
                <Route path="health-benefits-comparison" element={<HealthBenefitsComparison />} />

                <Route path="policy-register" element={<PolicyRegister />} />
                <Route path="gic-policy-report" element={<GICPolicyReport />} />
                <Route path="policy-not-received" element={<PolicyNotReceivedList />} />
                <Route path="tpa-master" element={<TPAMaster />} />

                <Route path="consolidated-renewal-list" element={<ConsolidatedRenewalList />} />
                <Route path="renewal-intimation" element={<RenewalIntimation />} />
                <Route path="endorsement-intimation" element={<EndorsementIntimation />} />

                <Route path="health-insurance-entry" element={<HealthInsuranceEntryList />} />
                <Route path="health-insurance-entry/add" element={<HealthInsuranceEntry />} />
                <Route path="health-insurance-entry/edit/:id" element={<HealthInsuranceEntry />} />
                <Route path="health-insurance-entry/view/:id" element={<HealthInsuranceEntry />} />
                <Route path="add-health-insurance-entry" element={<HealthInsuranceEntry />} />

                <Route path="motor-insurance-entry" element={<MotorInsuranceEntryList />} />
                <Route path="motor-insurance-entry/add" element={<MotorInsuranceEntry />} />
                <Route path="motor-insurance-entry/edit/:id" element={<MotorInsuranceEntry />} />
                <Route path="motor-insurance-entry/view/:id" element={<MotorInsuranceEntry />} />
              </Route>
            </Route>

            <Route path="/" element={<Home />} />
            <Route path="contact" element={<Contact />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* <Route path="demo" element={<DemoPage />} /> */}
            {/* <Route path="demo" element={<Navigate to="/dashboard" replace />} /> */}

          </Routes>
        </Suspense>
      </PdfJobProvider>
      </ToastProvider>
    </>
  );
}

export default App;
